
import { autoinject } from "aurelia-dependency-injection";
import { bindable, computedFrom } from "aurelia-framework";
import JSZip from "jszip";
import { PscCloseOutDocumentLink, ThumbnailClientModel } from "../../models/psc/psc.models";
import { ConfigService } from "../../services/basic/config.service";
import { AttachmentsClientService } from "../../services/attachments/attachments-client.service";
import { CacheClientService, CacheLocation } from "../../services/basic/client-caching.service";
import { EntityTypes } from "../../models/basic.models";
import { AttachmentsHelper } from "../../helpers/attachments.helper";
import { FileHelper } from "../../helpers/file-helper";
import { AttachmentType, AttachmentViewerDocument, AttachmentViewerNewTab } from "../../models/attachments.models";
import { PscService } from "../../services/psc/psc.service";

//Load styles
require("./partial-attachment-viewer.scss");

@autoinject
export class PartialAttachmentViewer {
    private _scale: number = 1;
    private _rotate: number = 0;
    private _commandInProgress: boolean = false;
    private _documents: AttachmentViewerDocument[];
    private _thumbnailFileName: string;

    public id: number;
    public type: string;
    public imageElement: HTMLImageElement;
    public zoomAmount: number = 100;
    public maxZoomOut: number = 20;
    public maxZoomIn: number = 200;
    public scaleAmount: number = 20;

    public attachmentType: AttachmentType;
    public thumbnails: ThumbnailClientModel[] = [];
    public selectedAttachment: any;
    public areThumbnailsLoading: boolean;
    public isAttachmentLoading: boolean;
    public pscCloseOutDocumentLinks: PscCloseOutDocumentLink[];
    public blob: string = ConfigService.instance().config.urls.blobStorageUrl;
    private _thumbnailImages: { name: string; url: string }[] = [];

    @bindable
    public isDrawer: boolean;

    @bindable
    public entityId: number;

    @bindable
    public entityTypeId: number;

    @bindable
    public getAttachmentDocuments: (() => Promise<AttachmentViewerDocument[]>);

    @bindable
    public newTabIdentifier: string;

    @bindable
    public selectedThumbnailId: string;

    @computedFrom('zoomAmount', 'maxZoomIn')
    get canZoomIn() {
        return this.zoomAmount < this.maxZoomIn; 
    }

    @computedFrom('zoomAmount', 'maxZoomOut')
    get canZoomOut() {
        return this.zoomAmount > this.maxZoomOut;
    }

    constructor(private attachmentService: AttachmentsClientService, private cacheService: CacheClientService, private pscService: PscService) {
    }
    
    activate(params) {
        if(params.thumbnailFileName){
            this._thumbnailFileName = params.thumbnailFileName;
        }
    }
    
    async attached() {
        if (this.getAttachmentDocuments) {
            await this.loadAttachments();
        } else if (this.entityTypeId === EntityTypes.PSC) {
            await this.getPscCloseOutAttachments();
        }
    }


    downloadFile(file) {
        let path;
        let fileName = "";
        if (file.originalAttachmentName) fileName = file.originalAttachmentName;
        if (file.sasUrl.startsWith("http")) {
            path = file.sasUrl;
        }
        else {
            path = AttachmentsHelper.getUrl(file.sasUrl, this.blob, null);
        }
        FileHelper.downloadFile(path, fileName, true);
    }

    addAttachmentToken(file)
    {
        return new Promise<string>((resolve, reject) => {
            if (file && file.path) {
                let path = AttachmentsHelper.getUrl(file.path, this.blob, null);
                this.attachmentService.getSharedAccessSignatureToken(file.entityAttachmentId, true).then(sasToken => {
                    if (sasToken) {
                        path += ((sasToken.startsWith("?") ? "" : "?") + sasToken);
                    }

                    file.fullUrl = AttachmentsHelper.getUrl(path, this.blob, null);

                    resolve(file.fullUrl);
                }).catch(e => reject(e));
            }
        });
    }

    async loadAttachments() {
        this.areThumbnailsLoading = true;
        this.isAttachmentLoading = true;

        this._documents = await this.getAttachmentDocuments();
        if (this.newTabIdentifier) {
            await this.cacheService.put<AttachmentViewerNewTab>(`attachmentResults-${this.newTabIdentifier}`, { documents: this._documents }, CacheLocation.LocalStorage);
        }

        this.thumbnails = this._documents.map(document => {
            return {
                thumbnailFileName: document.fileName,
                originalAttachmentName: document.originalAttachmentName,
                fileName: document.fileName,
                pageNumber: document.pageNumber,
                src: document.src,
                sasUrl: document.sasUrl,
                attachmentType: document.attachmentType,
                id: document.id
            };
        });
                
        this.isAttachmentLoading = false;
        this.areThumbnailsLoading = false;

        if (this.selectedThumbnailId) {
            const specificAttachment = this.thumbnails.find(thumbnail => thumbnail.id == this.selectedThumbnailId) ?? this.thumbnails[0];
            this.selectAttachment(specificAttachment);
        } else {
            this.selectAttachment(this.thumbnails[0]);
        }
    }

    async getPscCloseOutAttachments() {
        this.areThumbnailsLoading = true;
        this.isAttachmentLoading = true;

        //Get the close out document links
        try {
            this.pscCloseOutDocumentLinks =  (await this.pscService.getPscCloseOutDocumentLinks(this.entityId)).pscCloseOutDocumentLinks;
            this.isAttachmentLoading = false;
        }
        catch (error) {
            this.isAttachmentLoading = false;
            console.error(error);
        }

        //Get the thumbnail images
        try {
            const pscThumbnailZipFile = await this.pscService.getPscThumbnailZipFile(this.entityId);
            
            // Unzip using JSZip
            const zip = new JSZip();
            await zip.loadAsync(pscThumbnailZipFile);
            
            // Extract images and create URLs for them
            const imagePromises = [];
            zip.forEach((relativePath, zipEntry) => {
                if (!zipEntry.dir && /\.(jpg|jpeg|png|gif)$/.test(relativePath)) { // Filter for image types
                    imagePromises.push(zipEntry.async('blob').then((blob) => {
                        return {
                            name: relativePath, // Get the file name
                            url: URL.createObjectURL(blob) // Create a URL for the image blob,
                        };
                    }));
                }
            });
            
            this._thumbnailImages = await Promise.all(imagePromises); //Set the images array
            this.areThumbnailsLoading = false;
        }
        catch (error) {
            this.areThumbnailsLoading = false;
            console.error(error);
        }

        this.pscCloseOutDocumentLinks.forEach(link => {
            link.thumbnails.forEach((thumbnail) => {

                //Add thumbnail to deficiency based on the image name
                const image = this._thumbnailImages.find(x => thumbnail.thumbnailFileName.includes(x.name) || x.name.includes(thumbnail.thumbnailFileName));
                thumbnail.src = image?.url;

                //Add all the thumbnails to attachments array
                this.thumbnails.push(thumbnail);
                
                //Add sasUrl to the thumbnail
                this.thumbnails.forEach((thumbnail) => {
                    thumbnail.sasUrl = link.sasUrl;
                    const attachmemtType = this.isPDF(thumbnail.sasUrl) ? AttachmentType.Object : AttachmentType.Image;
                    thumbnail.attachmentType = attachmemtType;
                });
            });
        });

        if(this._thumbnailFileName){

            //Select specific attachment based on params
            const specificAttachment = this.thumbnails.find(thumbnail => (thumbnail.thumbnailFileName.includes(this._thumbnailFileName) || this._thumbnailFileName.includes(thumbnail.thumbnailFileName)));
            this.selectAttachment(specificAttachment);
        } else {

            //Select first attachment
            this.selectAttachment(this.thumbnails[0]);
        }
    }

    getFileUrl(file: ThumbnailClientModel) {
        if(file.src) return file.src;
        if(this.isPDF(file.sasUrl)){
            return require('/images/rs-inspection/rs-inspection-pdf.png');
        }
        if(this.isXls(file.sasUrl)){
            return require('/images/rs-inspection/rs-inspection-xls.png');
        }
        if(this.isDoc(file.sasUrl)){
            return require('/images/rs-inspection/rs-inspection-doc.png');
        }
        return require('/images/rs-inspection/rs-inspection-doc.png');
    }

    isPDF(link: string): boolean {
        const pdfPattern = /\.pdf/i;
        return pdfPattern.test(link);
    }

    isImage(link): boolean {
        const imageExtensions = ["jpg", "jpeg", "png", "gif", "svg"];
        return imageExtensions.some(imageExtension => link.toLowerCase().indexOf(imageExtension) != -1);
    }

    isXls(link): boolean {
        const extensions = ["xls"];
        return extensions.some(extension => link.toLowerCase().indexOf(extension) != -1);
    }

    isDoc(link): boolean {
        const extensions = ["doc"];
        return extensions.some(extension => link.toLowerCase().indexOf(extension) != -1);
    }

    public selectAttachment(attachment: any) {
        if (attachment && this.selectedAttachment == attachment) return;
        this.selectedAttachment = null; //Reset selection
        
        setTimeout(() => {
            this.selectedAttachment = attachment;
        });
        if (attachment) {
            this.attachmentType = attachment.attachmentType;
        }
        
        //Reset scale and rotate
        if(this.attachmentType == AttachmentType.Image){
            this.execCommand('restore');
        }
    }

    public execCommand(command) {

        if (this._commandInProgress) return;

        this._commandInProgress = true;
        setTimeout(() => { this._commandInProgress = false }, 200);

        switch (command) {
            case "restore": {
                this._scale = 1;
                this._rotate = 0;
                this.zoomAmount = 100;
            }
                break;
            case "zoom-in": {
                if(this.canZoomIn){
                    this._scale += this.scaleAmount / 100;
                    this.zoomAmount += this.scaleAmount;
                }
            }
                break;
            case "zoom-out": {
                if(this.canZoomOut){
                    this._scale -= this.scaleAmount / 100;
                    this.zoomAmount -= this.scaleAmount;
                }
            }
                break;
            case "rotate-right": {
                this._rotate += 90 % 360;
            }
                break;
            case "rotate-left": {
                this._rotate -= 90 % 360;
            }
                break;
        }

        if(this.imageElement){
            this.imageElement.style.transform = `scale(${this._scale}) rotate(${this._rotate}deg)`;
        }
    }
}